import React, { useContext, useEffect } from "react";

import { Grid, Tab, Tabs } from "@mui/material";
import { useHistory, useRouteMatch } from "react-router-dom";

import DocSearch from "../components/search/doc-search";
import IdentitySearch from "../components/search/identity-search";
import MediaSearch from "../components/search/media-search";
import OrgSearch from "../components/search/org-search";
import PublicLibrarySearch from "../components/search/public-library-search";
import UserSearch from "../components/search/user-search";
import TabPanel from "../components/shared/tab-panel";
import { OrgSearchQueryContext } from "../context/org-search-context";
import { SearchTabContext } from "../context/search-context";
import { UserSearchQueryContext } from "../context/user-search-context";
import useQuery from "../helpers/hooks/use-query";
import mainStyles from "../styles";
import ContentLibrarySearch from "components/search/content-library-search";
import GrsSearch from "components/search/grs-search";
import SensorsSearch from "components/search/sensors-search";
import TemplateSearch from "components/search/template-search";
import { GrsSearchQueryContext } from "context/grs-search-context";
import { IdentitySearchQueryContext } from "context/identity-search-context";
import { TemplateSearchQueryContext } from "context/template-search-context";

interface TabMatchParams {
  tab: string;
}

const Search: React.FC = () => {
  const query = useQuery();

  const classes = mainStyles();
  const { searchTab, setSearchTab, docSearchKey, userSearchKey, orgSearchKey } =
    useContext(SearchTabContext);
  const history = useHistory();

  const tabs = [
    {
      key: "user",
      label: "Users",
      component: <UserSearch key={userSearchKey} />,
      enabled: true,
    },
    {
      key: "identity",
      label: "Identity",
      component: <IdentitySearch />,
      enabled: true,
    },
    {
      key: "org",
      label: "Organization",
      component: <OrgSearch key={orgSearchKey} />,
      enabled: true,
    },
    {
      key: "doc",
      label: "Document",
      component: <DocSearch key={docSearchKey} />,
      enabled: true,
    },
    {
      key: "template",
      label: "Templates",
      component: <TemplateSearch />,
      enabled: true,
    },
    {
      key: "grs",
      label: "GRS",
      component: <GrsSearch />,
      enabled: true,
    },
    {
      key: "publiclibrary",
      label: "Public Library",
      component: <PublicLibrarySearch />,
      enabled: true,
    },
    {
      key: "contentlibrary",
      label: "Content Library",
      component: <ContentLibrarySearch />,
      enabled: true,
    },
    {
      key: "sensor",
      label: "Sensors",
      component: <SensorsSearch />,
      enabled: true,
    },
    {
      key: "media",
      label: "Media",
      component: <MediaSearch />,
      enabled: true,
    },
  ];

  const handleTabChange = (
    _event: React.ChangeEvent<unknown>,
    newValue: number
  ) => {
    setSearchTab(newValue);
    history.push(`/search/${tabs[newValue].key}`);
  };

  const { userSearchQueryState, setUserSearchQueryState } = useContext(
    UserSearchQueryContext
  );
  const { identitySearchQueryState, setIdentitySearchQueryState } = useContext(
    IdentitySearchQueryContext
  );
  const { orgSearchQueryState, setOrgSearchQueryState } = useContext(
    OrgSearchQueryContext
  );
  const { templateSearchQueryState, setTemplateSearchQueryState } = useContext(
    TemplateSearchQueryContext
  );
  const { grsSearchQueryState, setGrsSearchQueryState } = useContext(
    GrsSearchQueryContext
  );

  const tabMatch = useRouteMatch<TabMatchParams>("/search/:tab");
  useEffect(() => {
    const tabKey = tabMatch?.params.tab;
    const tabIndex = tabs.findIndex((t) => t.key === tabKey);
    setSearchTab(tabIndex >= 0 ? tabIndex : 0);

    const userId = query.get("userId");
    if (userId) {
      setUserSearchQueryState({
        ...userSearchQueryState,
        userId,
      });
    }
    const identityID = query.get("identityid");
    if (identityID) {
      setIdentitySearchQueryState({
        ...identitySearchQueryState,
        identityID,
      });
    }
    const roleId = query.get("roleId");
    if (roleId) {
      if (tabKey === "template") {
        setTemplateSearchQueryState({
          ...templateSearchQueryState,
          orgID: roleId,
        });
      } else if (tabKey === "grs") {
        setGrsSearchQueryState({
          ...grsSearchQueryState,
          orgID: roleId,
        });
      } else {
        setOrgSearchQueryState({
          ...orgSearchQueryState,
          roleId,
        });
      }
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Grid container sx={classes.basicSpacing}>
      <Grid container direction={"column"}>
        <Tabs
          value={searchTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          {tabs.map((tab) => {
            if (tab.enabled) {
              return <Tab label={tab.label} key={tab.label} />;
            }
            return undefined;
          })}
        </Tabs>
        {tabs.map((tab, index) => {
          return (
            <div key={tab.label}>
              <TabPanel value={searchTab} index={index}>
                {tab.component}
              </TabPanel>
            </div>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default Search;
